import { Campaign, Edit } from "@mui/icons-material";
import {
  Box,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { ResponsiveButtonLink } from "components/Button";
import { CardOutline } from "components/Card";
import { MainLayoutContainer } from "components/Layout";
import { RequirementBox } from "components/Requirements";
import { TagManager } from "components/TagManager";
import { UserWithPet } from "components/VetStore/CoachPetAvatar";
import {
  useGetAcademicYearsByIdLazyQuery,
  useGetGraduationClassesByIdLazyQuery,
} from "graphql/__generated__/hooks";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GraduationClass } from "types/graduationClass";
import { Requirement } from "types/requirement";
import ConfirmActionModal from "../../../modals/ConfirmActionModal/ConfirmActionModal";
import { resetPasswordRequestThunk } from "../../../reduxStore/auth/thunks";
import { showNotificationThunk } from "../../../reduxStore/alert/thunks";
import { NotificationSeverity } from "../../../helpers/constants";
import { useAppDispatch } from "../../../hooks/useAppDispatch";

export const GraduationClassDetail = () => {
  const { graduationClassId, organizationId } = useParams();
  const [getGraduationClassById, { data: dataGraduationClass }] =
    useGetGraduationClassesByIdLazyQuery();
  const [getAcademicYearsById, { data: dataAcademicYear }] =
    useGetAcademicYearsByIdLazyQuery();
  const [inviteUserModal, setInviteUserModal] = useState(false);
  const dispatch = useAppDispatch();

  const graduationClass = useMemo<GraduationClass | null>(() => {
    if (dataGraduationClass?.graduation_class_by_pk)
      return {
        ...dataGraduationClass.graduation_class_by_pk,
        students: dataGraduationClass.graduation_class_by_pk.students.map(
          (student) => student.student,
        ),
        tags: dataGraduationClass.graduation_class_by_pk.tags.map(
          (tag) => tag.tag,
        ),
      } as GraduationClass;
    return null;
  }, [dataGraduationClass]);

  useEffect(() => {
    if (dataGraduationClass?.graduation_class_by_pk) {
      getAcademicYearsById({
        variables: {
          academic_year_id:
            dataGraduationClass?.graduation_class_by_pk
              ?.current_academic_year_id,
        },
      });
    }
  }, [dataGraduationClass]);

  const academicYear = useMemo(() => {
    if (dataAcademicYear) {
      return dataAcademicYear?.academic_year_by_pk;
    }
  }, [dataAcademicYear]);

  useEffect(() => {
    if (graduationClassId) {
      getGraduationClassById({
        variables: { graduation_class_id: graduationClassId || "" },
      });
    }
  }, [graduationClassId]);

  if (!graduationClass) {
    return <div>loading...</div>;
  }

  const handleResendToAllLearners = async () => {
    for (const learner of graduationClass?.students || []) {
      await dispatch(
        resetPasswordRequestThunk({
          email: learner.email,
          isResendingInvite: true,
        }),
      );
    }
    dispatch(
      showNotificationThunk({
        message: "Invitation sent successfully",
        severity: NotificationSeverity.success,
      }),
    );
  };

  return (
    <MainLayoutContainer
      title={graduationClass.name}
      buttonActions={
        <ResponsiveButtonLink
          icon={<Edit />}
          to={`/organization/${organizationId}/graduation-class/${graduationClassId}/edit`}
        >
          Edit
        </ResponsiveButtonLink>
      }
      breadcrumb={BREADCRUMB_PAGE.GRADUATION_CLASS}
    >
      <Box>
        <CardOutline>
          <CardContent>
            <Typography variant="body1" gutterBottom component="div">
              <Typography variant="body1" component="span" fontWeight={600}>
                Start Date:
              </Typography>{" "}
              {graduationClass.start_date}
            </Typography>
            <Box mt={2}>
              <Stack spacing={1} direction={"row"}>
                <Typography gutterBottom component="div" fontWeight={600}>
                  Tags:
                </Typography>
                {graduationClass.tags && (
                  <TagManager tags={graduationClass.tags} isEditing={false} />
                )}
              </Stack>
            </Box>
          </CardContent>
        </CardOutline>

        <Box mt={2}>
          <CardOutline>
            <CardContent>
              <Typography gutterBottom component="div" fontWeight={600}>
                Program
              </Typography>

              <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                <strong>Name:</strong> {academicYear?.name}
              </Typography>

              <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                <strong>Start Date:</strong> {academicYear?.start_date} -{" "}
                <strong>End Date:</strong> {academicYear?.end_date}
              </Typography>

              <Box mt={2}>
                <Stack spacing={1} direction={"row"}>
                  <Typography gutterBottom component="div" fontWeight={600}>
                    Tags:
                  </Typography>
                  {academicYear?.tags && (
                    <TagManager
                      tags={academicYear?.tags.map((tag) => tag.tag)}
                      isEditing={false}
                    />
                  )}
                </Stack>
              </Box>

              <Box mt={2}>
                <Typography gutterBottom component="div" fontWeight={600}>
                  Requirements
                </Typography>

                {academicYear?.requirements.map((requirement) => (
                  <RequirementBox
                    key={requirement.requirement.requirement_id}
                    requirement={requirement.requirement as Requirement}
                  />
                ))}
              </Box>
            </CardContent>
          </CardOutline>
        </Box>
        <Box mt={2}>
          <CardOutline>
            <CardContent>
              <Box
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography gutterBottom component="div" fontWeight={600}>
                  Learners
                </Typography>

                <Tooltip title="Resend Email To All Learners">
                  <IconButton
                    onClick={() => {
                      setInviteUserModal(true);
                    }}
                    aria-label="resend-invite"
                    size={"large"}
                  >
                    <Campaign />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {graduationClass?.students?.map((student) => (
                      <TableRow key={student.user_id}>
                        <TableCell>
                          <UserWithPet
                            pictureUrl={student.picture_url}
                            name={student.name}
                            equippedStoreItems={student.equipped_store_items}
                            avatarSize={40}
                            showName
                          />
                        </TableCell>
                        <TableCell>
                          <Typography>{student.email}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </CardOutline>
        </Box>
      </Box>
      {inviteUserModal && (
        <ConfirmActionModal
          title={"Invite User"}
          message={`A formal email invitation will be sent to all Learners associated with ${graduationClass.name}.`}
          show={inviteUserModal}
          onConfirm={() => handleResendToAllLearners()}
          onDismiss={() => {
            setInviteUserModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
};
